/**
 * The five-step strip across the top of the letter generator.
 *
 * Split out of `pages/attest/LetterGenerator.tsx` under the 350-line rule in
 * `docs/FILE_SIZE_POLICY.md` (ATTEST-UX-2 Bundle 2). `STEPS` and `StepKey` live
 * here rather than on the page because three other modules key off these
 * strings — `refusalGuidance.ts` maps every refusal to one of them, and both
 * `requiredFields` and this nav bucket by them — and a shared vocabulary that
 * lives inside one screen's component file is a vocabulary waiting to drift.
 *
 * Navigation only. Every step but the first is disabled until a letter type is
 * chosen, because the type is what tells the later steps which assertions,
 * inputs and documents they are rendering; without it they have nothing to
 * draw. That is the *only* thing gated here — the strip does not enforce order,
 * and a CPA may jump back to a finished step at any time. What may not be
 * skipped is enforced by `StepContinue` and, finally, by the server.
 *
 * The one thing it now shows as well as offers is a done mark (Bundle 2 item 6).
 * Green means one thing across these screens — this part is finished — and the
 * strip is where a CPA already looks to ask "how far in am I?". It is a tick on
 * the button that is already there, not a second progress widget: the answer
 * belongs to the control that navigates, not beside it.
 *
 * What green does *not* mean is "this will be accepted". `completedSteps` counts
 * filled required fields and nothing else; consent, independence, the deny list
 * and every evidence gate are still decided by the server on the next call.
 */
import { Check } from 'lucide-react'

import { Button } from '@/components/ui/button'

export const STEPS = [
  { key: 'classify', label: '1. Classify the request' },
  { key: 'scope', label: '2. What may be said' },
  { key: 'details', label: '3. Letter and statements' },
  { key: 'independence', label: '4. Independence' },
  { key: 'letter', label: '5. Draft and download' },
] as const

export type StepKey = typeof STEPS[number]['key']

export interface WizardStepNavProps {
  current: StepKey
  onSelect: (key: StepKey) => void
  /** False until a letter type is chosen — see the module docstring. */
  canLeaveClassify: boolean
  /**
   * Steps whose required fields are all filled. From `completedSteps`, which is
   * the same derivation as the asterisks — see its docstring for why steps 2 and
   * 5 are never in here. Optional: a host that does not track it gets the strip
   * exactly as it was.
   */
  completed?: ReadonlySet<StepKey>
}

export function WizardStepNav({
  current, onSelect, canLeaveClassify, completed,
}: WizardStepNavProps) {
  return (
    <nav className="flex flex-wrap gap-2" aria-label="Letter steps">
      {STEPS.map(s => {
        const isCurrent = current === s.key
        const isDone = completed?.has(s.key) ?? false
        return (
          <Button
            key={s.key}
            size="sm"
            variant={isCurrent ? 'default' : 'outline'}
            data-testid={`step-${s.key}`}
            // Read, not decorative: the tick is `aria-hidden`, and a screen
            // reader gets the state from here instead.
            data-complete={isDone ? 'true' : undefined}
            // The green is on the outline only. On the current step the button
            // is already solid primary, and emerald text on it would be both
            // unreadable and a second claim on the eye — the tick alone carries
            // it there.
            className={isDone && !isCurrent
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 hover:text-emerald-900'
              : undefined}
            disabled={s.key !== 'classify' && !canLeaveClassify}
            onClick={() => onSelect(s.key)}
          >
            {isDone && (
              <>
                <Check
                  aria-hidden="true"
                  data-testid={`step-done-${s.key}`}
                  className="mr-1.5 h-3.5 w-3.5 shrink-0"
                />
                <span className="sr-only">Complete: </span>
              </>
            )}
            {s.label}
          </Button>
        )
      })}
    </nav>
  )
}
